import './ShareProjectShareForm.scss';
import { FormController } from '@form-ts/core';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { SearchIcon } from 'src/modules/common/icons/SearchIcon';
import { AuthUser } from 'src/modules/common/types/AuthUser';
import { Project } from 'src/modules/common/types/Project';
import { ProjectRole } from 'src/modules/common/types/ProjectRole';
import { CreatableSelectField } from 'src/modules/form/components/CreatableSelectField';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';
import { SHARE_PROJECT_FORM_SCHEMA } from 'src/modules/sharing-projects/constants/schema';
import { SharedUser } from 'src/modules/sharing-projects/types/SharedUser';
import { ShareProjectForm } from 'src/modules/sharing-projects/types/ShareProjectForm';
import { userInputError } from 'src/modules/sharing-projects/utils/userInputError';
import { OrganizationUser } from 'src/modules/users/types/OrganizationUser';

type Props = {
  readonly project: Project;
  readonly loggedInUser: AuthUser;
  readonly sharedUsers: ReadonlyArray<SharedUser>;
  readonly organizationUsers: ReadonlyArray<OrganizationUser>;
  readonly onSubmit: (form: FormController<ShareProjectForm, FormError>) => void;
};

export const ShareProjectShareForm = ({
  loggedInUser,
  sharedUsers,
  organizationUsers,
  project,
  onSubmit,
}: Props): React.ReactElement => {
  const intl = useIntl();

  const isOwner = loggedInUser.id === project.owner?.id;
  const isEditor = sharedUsers.some((user) => user.id === loggedInUser.id && user.role === ProjectRole.EDITOR);
  const canSetEditorRole = isOwner || isEditor;
  const roleOptions = canSetEditorRole
    ? [ProjectRole.EDITOR, ProjectRole.VIEWER]
    : [ProjectRole.VIEWER];
  const availableUsersToShare = useMemo(() => organizationUsers.filter(
    (organizationUser) => organizationUser.id !== loggedInUser.id &&
    sharedUsers.every((sharedUser) => sharedUser.id !== organizationUser.id),
  ).map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.email,
  })), [loggedInUser.id, organizationUsers, sharedUsers]);

  const form = useForm<ShareProjectForm, FormError>('shareProject.form', {
    reinitialize: false,
    initialValues: {
      role: canSetEditorRole ? ProjectRole.EDITOR : ProjectRole.VIEWER,
      user: null,
    },
  });

  const validationSchema = useMemo(() => SHARE_PROJECT_FORM_SCHEMA(
    sharedUsers
      .map((item) => item.email)
      .concat(project.owner ? [project.owner.email] : []),
  ), [sharedUsers, project]);
  useFormValidator(form, validationSchema);

  const submitting = useFormWatch(form, form.submitting.get);

  const handleSubmit = useCallback((event: React.FormEvent): void => {
    event.preventDefault();
    onSubmit(form);
  }, [form, onSubmit]);

  useEffect(() => {
    form.change({
      ...form.currentState,
      values: {
        ...form.currentState.values,
        role: canSetEditorRole ? ProjectRole.EDITOR : ProjectRole.VIEWER,
      },
    });
  }, [form, canSetEditorRole]);

  return (
    <form
      className="bp-share-project-share-form"
      onSubmit={handleSubmit}
    >
      <CreatableSelectField
        field={form.field.at('user')}
        options={availableUsersToShare}
        icon={<SearchIcon/>}
        placeholder={intl.formatMessage({ id: 'shareProject/modal/shareField/placeholder' })}
        isValidNewOption={(inputValue) => userInputError(inputValue, intl) === null}
        noOptionsMessage={({ inputValue }) => userInputError(inputValue, intl) ||
          intl.formatMessage(
            { id: 'shareProject/modal/input/validation/empty' },
            { value: inputValue },
          )}
        formatCreateLabel={(inputValue) => intl.formatMessage(
          { id: 'shareProject/modal/input/validation/valid' },
          { value: inputValue },
        )}
        isDisabled={submitting}
        filterOption={(option, search) => search.length >= 3 &&
          (option.label.toLowerCase().includes(search.toLowerCase()) ||
          option.value.toLowerCase().includes(search.toLowerCase()))}
      />

      <DropdownField
        size="lg"
        theme="gray"
        field={form.field.at('role').transform(ensureNonNull())}
        options={roleOptions}
        disabled={!canSetEditorRole}
        renderOption={((option) => (
          <FormattedMessage id={`shareProject/modal/${option}`}/>
        ))}
      />

      <Button
        className="bp-share-project-share-form__button"
        type="submit"
        size="lg"
        disabled={submitting}
        loading={submitting}
        onClick={handleSubmit}
        icon={(
          <div className="bp-share-project-share-form__button-icon">
            <ArrowRightIcon/>
          </div>
        )}
      >
        <FormattedMessage id="shareProject/modal/button/share"/>
      </Button>
    </form>
  );
};
